import type { LoroDoc } from 'loro-crdt'
import * as v from 'valibot'

import { LOGIC_OPTIONS } from '../Policy/contract'
import { fileHashSchema, pdfAnchorSchema } from '../Policy/messages'
import type { PdfAnchor } from '../Policy/types'

/* Shared vocabulary for one source's triage room (`SourceAgent`) and the
   registry (`SourceManagerAgent`). A source is a file, identified by its
   sha256 hash — also the Nucleus `doc_id` — and the room is named by it.
   Pure — the admin imports it through `@worker/policy-builder/client` and
   reads the room with the same code. */

export const SOURCE_MANAGER_ROOM = 'default'

/** A sha256 hex digest — the only legal source room name. */
export const isSourceHash = (id: string): boolean => v.is(fileHashSchema, id)

/** LoroMap on the `SourceManagerAgent` doc: hash -> `SourceSummary`. */
export const SOURCE_REGISTRY_CONTAINER = 'sources'

/** Loro containers in one source document. Only `notes` is client-editable. */
export const SOURCE_DOC = {
  meta: 'meta',
  neuro: 'neuro',
  candidates: 'candidates',
  references: 'references',
  activity: 'activity',
  notes: 'notes',
} as const

export const SOURCE_STAGES = ['queued', 'triaging', 'triaged'] as const
export type SourceStage = (typeof SOURCE_STAGES)[number]

export const SOURCE_STAGE_LABELS: Record<SourceStage, string> = {
  queued: 'Queued',
  triaging: 'Triaging',
  triaged: 'Triaged',
}

/** Legal stage moves. Anything not listed is refused by the agent. */
export const SOURCE_TRANSITIONS: Record<SourceStage, SourceStage[]> = {
  queued: ['triaging'],
  triaging: ['triaged'],
  triaged: ['triaging'],
}

export type NeuroStatus =
  | 'no-file'
  | 'not-ingested'
  | 'ingesting'
  | 'ready'
  | 'drafting'
  | 'proposed'
  | 'failed'

export interface SourceMeta {
  /** The file hash (sha256 hex) — also the Nucleus `doc_id` — and this room's name. */
  id: string
  /** Nucleus title. */
  title: string
  /** Primary reference's PaAPI source type level 1 label when a reference
   *  resolves; else Nucleus `category` (display only). */
  sourceTypeLevel1: string | null
  stage: SourceStage
  stageChangedAt: string
  lead: string | null
  /** Newest linked PaAPI reference_document_version id; `null` until a
   *  reference resolves. Library metadata only — PDF anchors identify their
   *  file by `documentHash`, never by version. */
  versionId: string | null
  createdAt: string
  /** The initiative, clients and committed date captured at intake (S1);
   *  `null` for a source added without them (the pre-S1 `/add` path, or a
   *  pull of an existing library document). */
  work: SourceWork | null
  /** Set when this source's name or URL matched another already in the
   *  library and the analyst said they are different documents (S10). */
  distinctFrom: SourceDistinctFrom | null
  /** The curation instruction last sent to Nucleus as a knowledge note, so
   *  each instruction reaches it once, whichever pull carries it. */
  curationNoted?: string
}

/** The initiating work behind a source: its Jira initiative link (or the
 *  explicit choice of none), the clients it's committed to, and the date
 *  committed to. Seeds the accepted policy's `committed-client`/
 *  `committed-date` proposals (S1). */
export interface SourceWork {
  initiativeKey: string | null
  clients: string[]
  committedDate: string | null
  /** S1: optional guidance for curating this source, captured once at
   *  intake. Also added as a Nucleus knowledge note so NURO reads it. */
  curationInstruction?: string | null
}

/** Why a source whose name or URL matched the library is a different
 *  document, not a duplicate (S10). */
export interface SourceDistinctFrom {
  ids: string[]
  reason: string
  by: string
  at: string
}

/** A source already in the library with the same name or URL as one being
 *  added — surfaced so the analyst can say "duplicate" or "distinct" (S10). */
export interface SourceMatch {
  id: string
  title: string
  url: string | null
  on: ('url' | 'name')[]
}

/** One PaAPI reference document version linked to this source. */
export interface SourceVersionRef {
  id: string
  effectiveDateStart: string | null
  changeLog: string | null
  createdAt: string | null
}

/** A PaAPI reference document this source's file backs, newest versions first. */
export interface SourceReference {
  /** PaAPI reference document id. */
  id: string
  name: string
  status: string | null
  sourceTypeLevel1: string | null
  sourceTypeLevel2: string | null
  /** Only the versions whose source document is this source's file, newest first. */
  versions: SourceVersionRef[]
}

/** A document NURO found the source referencing (LCD ↔ article, …). */
export interface NeuroCompanion {
  refId: string
  title: string
  kind: string
  status: string
  /** Nucleus doc id once ingested. */
  docId: string | null
}

export interface NeuroState {
  status: NeuroStatus
  jobId: string | null
  jobStage: string | null
  error: string | null
  cardVersion: string | null
  policySubject: string | null
  related: NeuroCompanion[]
  checkedAt: string | null
}

export const NO_NEURO: NeuroState = {
  status: 'not-ingested',
  jobId: null,
  jobStage: null,
  error: null,
  cardVersion: null,
  policySubject: null,
  related: [],
  checkedAt: null,
}

export const TRIAGE_STATUSES = [
  'proposed',
  'accepted',
  'rejected',
  'deferred',
] as const
export type CandidateStatus = (typeof TRIAGE_STATUSES)[number]

/** NURO's measured claim-line scope. `summary` is a fixed backend template: render it verbatim. */
export interface CandidateImpact {
  ok: boolean
  summary: string
  lineCount: number
  reason: string | null
}

export interface CodeSuggestion {
  /** Nucleus dataset-binding key, e.g. `cpt_cmt_codes`. */
  key: string
  /** The scoping contract row these codes answer, when one does. */
  fieldId: 'procedure-codes' | 'diagnosis-codes' | null
  values: string[]
  source: string
}

export interface CandidateExcerpt {
  text: string
  cite: string
  anchor: PdfAnchor | null
}

/** One possible policy in the source, proposed by NURO or added by hand. */
export interface Candidate {
  /** `${meta.id}:${variantId}` for NURO candidates; UUID for manual rows. */
  id: string
  /** Nucleus variant id; `null` for a hand-added candidate. */
  variantId: string | null
  title: string
  /** NURO's own label; seeds `logicTypes` and nothing else. */
  editType: string
  /** PaAPI topic name, chosen in triage. */
  topic: string
  /** Logic types (PaAPI edit types), chosen in triage. */
  logicTypes: string[]
  rationale: string
  excerpt: CandidateExcerpt | null
  impact: CandidateImpact | null
  codes: CodeSuggestion[]
  cardVersion: string | null
  status: CandidateStatus
  reason: string | null
  policyId: string | null
  createdBy: string
  createdAt: string
  decidedBy: string | null
  decidedAt: string | null
  /** The Nucleus scoping suggestion last offered to this candidate's policy;
   *  the same suggestion is never offered twice. */
  scopingSuggestionId?: string
  /** Triage's free-text context for whoever scopes this proposal. */
  note?: string
}

/** `GET`/`POST /scoping` on a source room: NURO's scoping suggestion for one
 *  accepted candidate. Never an error for a slow or busy Nucleus: `pending`
 *  means poll again (with `requestId` when set). */
export interface SourceScoping {
  state: 'absent' | 'pending' | 'current' | 'stale'
  client: { key: string; name: string } | null
  computedAt: string | null
  /** Suggestions this call added to the policy. */
  added: number
  clients: { key: string; name: string }[]
  /** Set while a requested recompute is still queued; poll with it. */
  requestId: string | null
}

export interface SourceActivity {
  at: string
  by: string
  text: string
}

/** What the registry holds per source — enough for the queue. */
export interface SourceSummary {
  id: string
  title: string
  sourceTypeLevel1: string | null
  stage: SourceStage
  stageChangedAt: string
  lead: string | null
  neuro: NeuroStatus
  /** Live Nucleus job stage, from `neuro.jobStage`. */
  jobStage: string | null
  /** From `neuro.error`. */
  error: string | null
  candidates: Record<CandidateStatus, number>
  policyIds: string[]
  /**
   * S2: aggregate measured claim-line scope across this source's still-live
   * candidates (not rejected — a dismissed candidate's claim lines are no
   * longer real exposure). Sums only `impact.ok` candidates; `null` when
   * none of them have a usable measurement yet, so the queue can sort an
   * unmeasured source last rather than reading it as zero exposure.
   * Optional: a realtime-map entry mirrored before this field existed reads
   * as `undefined`, which the queue treats the same as `null`.
   */
  exposureLineCount?: number | null
  updatedAt: string
  /** The initiative, clients and committed date this source was added for
   *  (S1); `null` when it wasn't. */
  work: SourceWork | null
}

/** Plain-object read of one source document. */
export interface SourceDocView {
  meta: SourceMeta
  neuro: NeuroState
  candidates: Candidate[]
  references: SourceReference[]
  activity: SourceActivity[]
}
const EMPTY_META: SourceMeta = {
  id: '',
  title: '',
  sourceTypeLevel1: null,
  stage: 'queued',
  stageChangedAt: '',
  lead: null,
  versionId: null,
  createdAt: '',
  work: null,
  distinctFrom: null,
}

const isStage = (value: unknown): value is SourceStage =>
  typeof value === 'string' &&
  (SOURCE_STAGES as readonly string[]).includes(value)

/** Reads every container into plain data; an unseeded document reads as defaults. */
export const readSourceDoc = (doc: LoroDoc): SourceDocView => {
  const meta = doc.getMap(SOURCE_DOC.meta).toJSON() as Partial<SourceMeta>
  return {
    meta: {
      ...EMPTY_META,
      ...meta,
      stage: isStage(meta.stage) ? meta.stage : 'queued',
    },
    neuro: {
      ...NO_NEURO,
      ...(doc.getMap(SOURCE_DOC.neuro).toJSON() as Partial<NeuroState>),
    },
    candidates: (
      doc.getMovableList(SOURCE_DOC.candidates).toArray() as LegacyCandidate[]
    ).map(readCandidate),
    references: doc
      .getMovableList(SOURCE_DOC.references)
      .toArray() as SourceReference[],
    activity: doc
      .getMovableList(SOURCE_DOC.activity)
      .toArray() as SourceActivity[],
  }
}

export const summarizeSource = (
  view: SourceDocView,
  updatedAt: string,
): SourceSummary => {
  const candidates: Record<CandidateStatus, number> = {
    proposed: 0,
    accepted: 0,
    rejected: 0,
    deferred: 0,
  }
  for (const candidate of view.candidates) candidates[candidate.status] += 1
  const measured = view.candidates
    .filter((c) => c.status !== 'rejected')
    .flatMap((c) => (c.impact?.ok ? [c.impact.lineCount] : []))
  return {
    id: view.meta.id,
    title: view.meta.title,
    sourceTypeLevel1: view.meta.sourceTypeLevel1,
    stage: view.meta.stage,
    stageChangedAt: view.meta.stageChangedAt,
    lead: view.meta.lead,
    neuro: view.neuro.status,
    jobStage: view.neuro.jobStage,
    error: view.neuro.error,
    candidates,
    policyIds: view.candidates.flatMap((c) => (c.policyId ? [c.policyId] : [])),
    exposureLineCount: measured.length
      ? measured.reduce((sum, n) => sum + n, 0)
      : null,
    updatedAt,
    work: view.meta.work,
  }
}

/** What triage starts a candidate on: NURO's edit type, when it is a logic type. */
export const initialLogicTypes = (editType: string): string[] =>
  LOGIC_OPTIONS.includes(editType) ? [editType] : []

/* Candidates stored before triage chose a topic and logic types lack both. */
type LegacyCandidate = Omit<Candidate, 'topic' | 'logicTypes'> &
  Partial<Pick<Candidate, 'topic' | 'logicTypes'>>
const readCandidate = (candidate: LegacyCandidate): Candidate =>
  candidate.topic !== undefined && candidate.logicTypes
    ? (candidate as Candidate)
    : {
        ...candidate,
        topic: candidate.topic ?? '',
        logicTypes:
          candidate.logicTypes ?? initialLogicTypes(candidate.editType),
      }

/** Why a candidate cannot be accepted yet: PaAPI needs a topic and edit types. */
export const acceptBlockers = (
  candidate: Pick<Candidate, 'topic' | 'logicTypes'>,
): string[] => [
  ...(candidate.topic.trim() ? [] : ['Pick a topic']),
  ...(candidate.logicTypes.length ? [] : ['Pick at least one logic type']),
]

/** Why triage cannot be marked complete; empty when it can. */
export const completeBlockers = (view: SourceDocView): string[] => {
  const proposed = view.candidates.filter((c) => c.status === 'proposed').length
  return proposed ? [`${proposed} candidate(s) still proposed`] : []
}

/* Wire vocabulary for socket commands. No variant carries an actor:
   attribution always comes from the verified connection, never from a
   payload. Anything that calls Nucleus is an HTTP route on the room instead,
   because only an HTTP request carries the user's own token. */

const text = v.pipe(v.string(), v.trim(), v.minLength(1))
const stage = v.optional(v.picklist(SOURCE_STAGES))
const note = v.pipe(v.string(), v.trim(), v.maxLength(2000))

export const candidateExcerptSchema = v.object({
  text: v.string(),
  cite: v.string(),
  anchor: v.nullable(pdfAnchorSchema),
})

export const SourceMessageSchema = v.variant('type', [
  v.object({
    type: v.literal('claim'),
    expectedStage: stage,
  }),
  v.object({
    type: v.literal('reassign'),
    lead: v.nullable(text),
  }),
  v.object({
    type: v.literal('complete'),
    expectedStage: stage,
  }),
  v.object({
    type: v.literal('reopen'),
    expectedStage: stage,
  }),
  v.object({
    type: v.literal('add-candidate'),
    title: v.pipe(text, v.maxLength(200)),
    editType: v.pipe(v.string(), v.trim(), v.maxLength(200)),
    excerpt: v.optional(candidateExcerptSchema),
    /** What the policy should do, in the analyst's words (S-N20); seeds intent. */
    description: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(4000))),
  }),
  v.object({
    type: v.literal('decide'),
    candidateId: text,
    status: v.picklist(TRIAGE_STATUSES),
    reason: v.optional(note),
  }),
  v.object({
    type: v.literal('defer-rest'),
    reason: v.optional(note),
  }),
  v.object({
    type: v.literal('note'),
    candidateId: text,
    note,
  }),
  /* Either half alone: picking a topic must not resend logic types read from
     a copy the room has not echoed back yet, and vice versa. */
  v.object({
    type: v.literal('classify'),
    candidateId: text,
    topic: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(200))),
    logicTypes: v.optional(
      v.pipe(
        v.array(v.picklist(LOGIC_OPTIONS)),
        v.check(
          (list) => new Set(list).size === list.length,
          'Each logic type once',
        ),
      ),
    ),
  }),
])
export type SourceMessage = v.InferOutput<typeof SourceMessageSchema>
